import { useEffect, useMemo, useState } from 'react'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import { useTranslation } from '../contexts/TranslationContext'
import { ui } from '../styles/ui'
import { apiClient } from '../services/api'
import productTranslationCacheService from '../services/productTranslationCacheService'
import { formatDateTime } from '../utils/date'

const SORTABLE_FIELDS = ['normalized_source', 'translated_name', 'category_name', 'confidence', 'cache_hit_count', 'updated_at', 'created_at']

export default function ProductTranslationCacheAdmin() {
  const [stats, setStats] = useState({ count: 0, cache_hits_total: 0 })
  const [items, setItems] = useState([])
  const [page, setPage] = useState(1)
  const [perPage] = useState(25)
  const [loading, setLoading] = useState(false)
  const [confirmClear, setConfirmClear] = useState(false)
  const [categories, setCategories] = useState([])
  const [editingKey, setEditingKey] = useState('')
  const [sortBy, setSortBy] = useState('updated_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const [filters, setFilters] = useState({
    search: '',
    normalized_source: '',
    translated_name: '',
    source_language: '',
    target_language: '',
    category_name: '',
    translated_empty: 'all',
    confidence_min: '',
    confidence_max: '',
    cache_hits_min: '',
    cache_hits_max: '',
  })
  const [editForm, setEditForm] = useState({
    translated_name: '',
    category_id: '',
    source_language: '',
    target_language: 'en',
    confidence: '',
  })
  const { t } = useTranslation()

  const categoryByName = useMemo(() => new Map(categories.map((c) => [c.name, c])), [categories])

  useEffect(() => {
    loadStats()
    loadCategories()
  }, [])

  useEffect(() => {
    loadItems(page)
  }, [page, sortBy, sortOrder, filters])

  async function loadCategories() {
    try {
      const res = await apiClient.get('/categories', { params: { type: 'product', per_page: 1000 } })
      const list = res?.data?.data || res?.data?.categories || []
      setCategories(Array.isArray(list) ? list : [])
    } catch (_err) {
      setCategories([])
    }
  }

  async function loadStats() {
    try {
      const res = await productTranslationCacheService.stats()
      setStats(res.data || { count: 0, cache_hits_total: 0 })
    } catch (_err) {
      setStats({ count: 0, cache_hits_total: 0 })
    }
  }

  async function loadItems(nextPage = 1) {
    setLoading(true)
    try {
      const res = await productTranslationCacheService.list(nextPage, perPage, filters.search, {
        normalized_source: filters.normalized_source || undefined,
        translated_name: filters.translated_name || undefined,
        source_language: filters.source_language || undefined,
        target_language: filters.target_language || undefined,
        category_name: filters.category_name || undefined,
        translated_empty: filters.translated_empty || 'all',
        confidence_min: filters.confidence_min || undefined,
        confidence_max: filters.confidence_max || undefined,
        cache_hits_min: filters.cache_hits_min || undefined,
        cache_hits_max: filters.cache_hits_max || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      })
      setItems(res.data.items || [])
    } finally {
      setLoading(false)
    }
  }

  function toggleSort(field) {
    if (!SORTABLE_FIELDS.includes(field)) return
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
    setPage(1)
  }

  async function removeOne(normalizedSource) {
    if (!confirm(t('Are you sure?'))) return
    await productTranslationCacheService.remove(normalizedSource)
    await loadStats()
    await loadItems(page)
  }

  async function clearAll() {
    setConfirmClear(false)
    await productTranslationCacheService.clearAll()
    await loadStats()
    setPage(1)
    await loadItems(1)
  }

  function startEdit(item) {
    const category = categoryByName.get(item.category_name || '')
    setEditingKey(item.normalized_source)
    setEditForm({
      translated_name: item.translated_name || '',
      category_id: category?.id || '',
      source_language: item.source_language || '',
      target_language: item.target_language || 'en',
      confidence: item.confidence == null ? '' : String(item.confidence),
    })
  }

  async function saveEdit() {
    if (!editingKey) return
    const selectedCategory = categories.find((c) => c.id === editForm.category_id)
    await productTranslationCacheService.update(editingKey, {
      translated_name: editForm.translated_name || null,
      category_id: selectedCategory?.id || null,
      category_name: selectedCategory?.name || null,
      source_language: editForm.source_language || null,
      target_language: editForm.target_language || 'en',
      confidence: editForm.confidence === '' ? null : Number(editForm.confidence),
    })
    setEditingKey('')
    await loadItems(page)
  }

  async function regenerateOne(normalizedSource) {
    if (!confirm(t('Regenerate this cache row via LLM?'))) return
    await productTranslationCacheService.regenerate(normalizedSource)
    await loadItems(page)
  }

  function sortMarker(field) {
    if (sortBy !== field) return ''
    return sortOrder === 'asc' ? ' (asc)' : ' (desc)'
  }

  return (
    <PageShell>
      <PageHeader
        title={t('Hybrid Categorization Cache')}
        actions={
          <Button variant="danger" onClick={() => setConfirmClear(true)}>
            {t('Clear cache')}
          </Button>
        }
      />

      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('Cached items')}</p>
            <p className="text-lg font-semibold text-foreground">{stats?.count ?? 0}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('Total cache hits')}</p>
            <p className="text-lg font-semibold text-foreground">{stats?.cache_hits_total ?? 0}</p>
          </div>
          <div>
            <label className={ui.label}>{t('Search')}</label>
            <input className={ui.input} value={filters.search} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, search: e.target.value })) }} />
          </div>
          <div>
            <label className={ui.label}>{t('Translated Empty')}</label>
            <select className={ui.select} value={filters.translated_empty} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, translated_empty: e.target.value })) }}>
              <option value="all">{t('All', 'All')}</option>
              <option value="yes">{t('Only empty', 'Only empty')}</option>
              <option value="no">{t('Only filled', 'Only filled')}</option>
            </select>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <input className={ui.input} placeholder={t('Normalized source', 'Normalized source')} value={filters.normalized_source} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, normalized_source: e.target.value })) }} />
          <input className={ui.input} placeholder={t('Translated name', 'Translated name')} value={filters.translated_name} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, translated_name: e.target.value })) }} />
          <input className={ui.input} placeholder={t('Source language', 'Source language')} value={filters.source_language} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, source_language: e.target.value })) }} />
          <input className={ui.input} placeholder={t('Target language', 'Target language')} value={filters.target_language} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, target_language: e.target.value })) }} />
          <div className="relative">
            <input
              className={ui.input}
              list="category-filter-list"
              placeholder={t('Category', 'Category')}
              value={filters.category_name}
              onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, category_name: e.target.value })) }}
            />
            <datalist id="category-filter-list">
              <option value="__uncategorized__">{t('Uncategorised', 'Uncategorised')}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.name} />
              ))}
            </datalist>
          </div>
          <input className={ui.input} placeholder={t('Confidence min', 'Confidence min')} value={filters.confidence_min} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, confidence_min: e.target.value })) }} />
          <input className={ui.input} placeholder={t('Confidence max', 'Confidence max')} value={filters.confidence_max} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, confidence_max: e.target.value })) }} />
          <input className={ui.input} placeholder={t('Cache hits min', 'Cache hits min')} value={filters.cache_hits_min} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, cache_hits_min: e.target.value })) }} />
          <input className={ui.input} placeholder={t('Cache hits max', 'Cache hits max')} value={filters.cache_hits_max} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, cache_hits_max: e.target.value })) }} />
        </div>
      </Card>

      <div className={ui.card}>
        {loading ? (
          <div className="flex items-center justify-center py-10"><div className={ui.spinner} /></div>
        ) : (
          <table className={ui.table}>
            <thead className={ui.tableHead}>
              <tr>
                <th className={`${ui.tableHeadCell} cursor-pointer`} onClick={() => toggleSort('normalized_source')}>{t('Normalized Source', 'Normalized Source')}{sortMarker('normalized_source')}</th>
                <th className={`${ui.tableHeadCell} cursor-pointer`} onClick={() => toggleSort('translated_name')}>{t('Translated', 'Translated')}{sortMarker('translated_name')}</th>
                <th className={`${ui.tableHeadCell} cursor-pointer`} onClick={() => toggleSort('category_name')}>{t('Category', 'Category')}{sortMarker('category_name')}</th>
                <th className={`${ui.tableHeadCell} hidden lg:table-cell cursor-pointer`} onClick={() => toggleSort('confidence')}>{t('Confidence', 'Confidence')}{sortMarker('confidence')}</th>
                <th className={`${ui.tableHeadCell} hidden xl:table-cell cursor-pointer`} onClick={() => toggleSort('cache_hit_count')}>{t('Cache Hits', 'Cache Hits')}{sortMarker('cache_hit_count')}</th>
                <th className={`${ui.tableHeadCell} hidden lg:table-cell cursor-pointer`} onClick={() => toggleSort('updated_at')}>{t('Updated', 'Updated')}{sortMarker('updated_at')}</th>
                <th className={ui.tableHeadCell}></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {items.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-8 text-center text-sm text-muted-foreground">{t('No cached items')}</td></tr>
              ) : items.map((item) => {
                const missingTranslated = !item.translated_name || !String(item.translated_name).trim()
                const normalizedSource = String(item.normalized_source || '').trim()
                return (
                  <tr
                    key={item.normalized_source}
                    className={`${ui.tableRow} ${missingTranslated ? '!bg-red-50 hover:!bg-red-50' : ''}`}
                  >
                    <td className={`${ui.tableCell} align-top`}><span className="text-xs break-all">{item.normalized_source}</span></td>
                    <td className={`${ui.tableCellText} align-top whitespace-normal break-words ${missingTranslated ? 'text-red-700 font-semibold' : ''}`}>
                      {editingKey === item.normalized_source
                        ? <input className={ui.input} value={editForm.translated_name} onChange={(e) => setEditForm((prev) => ({ ...prev, translated_name: e.target.value }))} />
                        : (item.translated_name || t('MISSING', 'MISSING'))}
                    </td>
                    <td className={`${ui.tableCellText} align-top whitespace-normal break-words`}>
                      {editingKey === item.normalized_source ? (
                        <select className={ui.select} value={editForm.category_id} onChange={(e) => setEditForm((prev) => ({ ...prev, category_id: e.target.value }))}>
                          <option value="">-</option>
                          {categories.map((category) => (
                            <option key={category.id} value={category.id}>{category.name}</option>
                          ))}
                        </select>
                      ) : (item.category_name || '-')}
                    </td>
                    <td className={`${ui.tableCellText} hidden lg:table-cell align-top`}>
                      {editingKey === item.normalized_source
                        ? <input className={ui.input} value={editForm.confidence} onChange={(e) => setEditForm((prev) => ({ ...prev, confidence: e.target.value }))} />
                        : (item.confidence ?? '-')}
                    </td>
                    <td className={`${ui.tableCellText} hidden xl:table-cell align-top`}>{item.cache_hit_count ?? 0}</td>
                    <td className={`${ui.tableCellText} hidden lg:table-cell align-top`}>{item.updated_at ? formatDateTime(item.updated_at) : '-'}</td>
                    <td className={`${ui.tableCell} align-top`}>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {editingKey === item.normalized_source ? (
                          <>
                            <button className={ui.buttonSmPrimary} onClick={saveEdit}>{t('Save')}</button>
                            <button className={ui.buttonSmGhost} onClick={() => setEditingKey('')}>{t('Cancel')}</button>
                          </>
                        ) : (
                          <button className={ui.buttonSmPrimary} onClick={() => startEdit(item)}>{t('Edit')}</button>
                        )}
                        <button className={ui.buttonSmPrimary} onClick={() => regenerateOne(item.normalized_source)}>{t('Regenerate')}</button>
                        <button className={ui.buttonSmDanger} onClick={() => removeOne(item.normalized_source)}>{t('Delete')}</button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {items.length > 0 && (
        <div className="mt-4 flex items-center gap-3">
          <button className={ui.buttonGhost} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</button>
          <button className={ui.buttonGhost} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>{t('Previous', 'Previous')}</button>
          <span className="text-sm text-muted-foreground">{t('Page')} {page}</span>
          <button className={ui.buttonGhost} disabled={items.length < perPage} onClick={() => setPage((p) => p + 1)}>{t('Next', 'Next')}</button>
        </div>
      )}

      <ConfirmDialog open={confirmClear} onClose={() => setConfirmClear(false)} onConfirm={clearAll} title={t('Clear product translation cache')}>
        {t('Are you sure you want to clear all product translation cache entries?')}
      </ConfirmDialog>
    </PageShell>
  )
}
